import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { catchError, map, switchMap, tap, withLatestFrom, exhaustMap, filter } from 'rxjs/operators'
import { ProfilesApiService } from './profiles-api.service'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src'
import { of, zip } from 'rxjs'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { GbSelectGuestDialogComponent } from 'libs/gb-front/volunteer/feature/src/lib/gb-select-guest-dialog/gb-select-guest-dialog.component'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import * as ProfilesActions from './profiles.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import { GbRegisterDialogComponent } from 'libs/gb-front/auth/feature/register/src/lib/gb-register-dialog/gb-register-dialog.component'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import * as SelectedItemActions from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.actions'
import { S } from '@angular/cdk/keycodes'

@Injectable()
export class ProfilesEffects {
  loadProfilesVolunteer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfilesActions.loadProfilesVolunteer),
      tap(() => this.loaderService.setLoading(LoaderActionTypeEnum.LoadProfiles)),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) => {
        return this.profilesApiService.getProfiles(urlName, action.limit, action.offset, action.search).pipe(
          map((response) => {
            return ProfilesActions.loadProfilesVolunteerSuccess({
              profiles: response.results,
              count: response.count,
            })
          }),
          catchError(async (error) => ProfilesActions.loadProfilesVolunteerFailure({ error })),
        )
      }),
    ),
  )

  hideLoadProfilesLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfilesActions.loadProfilesVolunteerSuccess, ProfilesActions.loadProfilesVolunteerFailure),
        map(() => this.loaderService.setLoadingComplete(LoaderActionTypeEnum.LoadProfiles)),
      ),
    { dispatch: false },
  )

  openGuestSelect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProfilesActions.openGuestSelect),
      exhaustMap((action) => {
        return zip([of(action), this.gbFrontDialogService.getDialogObs(GbSelectGuestDialogComponent)])
      }),
      filter(([_, result]) => !!result),
      switchMap(([action, result]) => {
        const actions: Action[] = []
        if (result === 'success' && action.onSelectGuestActions) {
          actions.push(...action.onSelectGuestActions)
        }

        if (result === 'register') {
          //on register click
          actions.push(
            ProfilesActions.openRegisterGuestDialog({
              onSelectGuestActions: action.onSelectGuestActions,
            }),
          )
        } else if (!action.skipProfileCheck) {
          const onSuccessActions = action.onSelectGuestActions ?? []
          const onSuccess = () => onSuccessActions[0] ?? undefined
          actions.push(SelectedProfileActions.checkProfile(onSuccess))
        }
        return actions
      }),
    )
  })

  openRegisterGuestDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfilesActions.openRegisterGuestDialog),
      exhaustMap((action) => {
        return zip([
          of(action),
          this.gbFrontDialogService.getDialogObs(GbRegisterDialogComponent, {
            disableClose: true,
          }),
        ])
      }),
      filter(([, result]) => !!result),
      // on success (register)
      map(([action]) =>
        ProfilesActions.checkVolunteerVerification({
          onSelectGuestActions: action.onSelectGuestActions,
        }),
      ),
    ),
  )

  checkVolunteerVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfilesActions.checkVolunteerVerification),
      withLatestFrom(this.authFacade.authProfile$),
      map(([action, authProfile]) => {
        // TODO: refactor the openRegisterVerifyDialog and checkProfile to accept an array of actions instead of just one
        const onSuccessActions = action.onSelectGuestActions ?? []
        const onSuccess = () => onSuccessActions[0] ?? undefined

        if (authProfile?.enable_verification) return AuthActions.openRegisterVerifyDialog(onSuccess)
        return SelectedProfileActions.checkProfile(onSuccess)
      }),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private profilesApiService: ProfilesApiService,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private loaderService: GbLoaderService,
    private gbFrontDialogService: GbFrontDialogService,
  ) {}
}
